import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ArrowLeft } from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";

const CONTENT = {
  fr: {
    eyebrow: "Données personnelles",
    title: "Politique de confidentialité",
    sections: [
      {
        h: "Responsable du traitement",
        body: (
          <p>
            Le responsable de traitement des données collectées via le présent
            site est Simon Marie, éditeur du site HorsIndex. HorsIndex est
            actuellement un site de présentation et de ressources consacré à
            l'exposition numérique : le formulaire du site sert uniquement à la
            prise de contact.
          </p>
        ),
      },
      {
        h: "Données collectées",
        body: (
          <>
            <p className="mb-4">
              Les données collectées via les formulaires se limitent strictement aux
              éléments suivants :
            </p>
            <ul className="space-y-2 list-none">
              <li className="flex gap-3"><span className="text-[#8B8F95]">-</span> Nom et prénom</li>
              <li className="flex gap-3"><span className="text-[#8B8F95]">-</span> Adresse électronique</li>
              <li className="flex gap-3"><span className="text-[#8B8F95]">-</span> Lien, URL ou entité concernée, communiqués librement (facultatif)</li>
              <li className="flex gap-3"><span className="text-[#8B8F95]">-</span> Message ou contexte rédigé librement</li>
            </ul>
          </>
        ),
      },
      {
        h: "Finalités",
        body: (
          <p>
            Ces données sont collectées dans le seul objectif de prendre contact
            avec la personne concernée et de répondre à sa demande. Elles ne
            sont pas utilisées à des fins de prospection commerciale.
          </p>
        ),
      },
      {
        h: "Base juridique",
        body: (
          <p>
            Le traitement repose sur le consentement explicite de la personne concernée,
            matérialisé par l'envoi volontaire du formulaire, ainsi que sur l'intérêt
            légitime du responsable de traitement à répondre aux sollicitations qui lui
            sont adressées.
          </p>
        ),
      },
      {
        h: "Durée de conservation",
        body: (
          <p>
            Les messages reçus sont conservés pour la durée strictement
            nécessaire au traitement de la demande, sans excéder trois années à
            compter du dernier échange.
          </p>
        ),
      },
      {
        h: "Droits de la personne concernée",
        body: (
          <>
            <p className="mb-4">
              Conformément au Règlement général sur la protection des données (RGPD),
              chaque personne dispose des droits suivants :
            </p>
            <ul className="space-y-2 list-none">
              <li className="flex gap-3"><span className="text-[#8B8F95]">-</span> Droit d'accès aux données</li>
              <li className="flex gap-3"><span className="text-[#8B8F95]">-</span> Droit de rectification</li>
              <li className="flex gap-3"><span className="text-[#8B8F95]">-</span> Droit d'opposition</li>
              <li className="flex gap-3"><span className="text-[#8B8F95]">-</span> Droit à la limitation</li>
              <li className="flex gap-3"><span className="text-[#8B8F95]">-</span> Droit à l'effacement</li>
              <li className="flex gap-3"><span className="text-[#8B8F95]">-</span> Droit à la portabilité</li>
              <li className="flex gap-3"><span className="text-[#8B8F95]">-</span> Droit d'introduire une réclamation auprès de l'autorité de contrôle compétente</li>
            </ul>
          </>
        ),
      },
      {
        h: "Exercice des droits",
        body: (
          <p>
            L'exercice de ces droits s'effectue par courriel à{" "}
            <a
              href="mailto:horsindex@gmail.com"
              className="text-[#181A1D] underline-link"
            >
              horsindex@gmail.com
            </a>.
            Une preuve d'identité pourra être demandée pour confirmer la
            légitimité de la requête.
          </p>
        ),
      },
      {
        h: "Sous-traitants techniques",
        body: (
          <>
            <p className="mb-4">
              Le site est hébergé par Netlify (États-Unis, certifié EU-US Data
              Privacy Framework). La transmission des formulaires repose sur
              Netlify Forms, exclusivement pour acheminer les messages reçus
              au responsable de traitement. Aucune donnée n'est exploitée à des fins de
              profilage, de revente, ni de prospection automatisée.
            </p>
            <p>
              Le service de polices typographiques Google Fonts est appelé pour
              le seul rendu visuel ; aucun cookie tiers n'est posé par le site.
            </p>
          </>
        ),
      },
      {
        h: "Cookies",
        body: (
          <p>
            Le site ne dépose pas de cookies publicitaires ni de traceurs de profilage
            commercial. Des cookies techniques strictement nécessaires au fonctionnement
            du site peuvent être utilisés.
          </p>
        ),
      },
    ],
  },
  en: {
    eyebrow: "Personal data",
    title: "Privacy policy",
    sections: [
      {
        h: "Data controller",
        body: (
          <p>
            The controller of data collected via this site is Simon Marie,
            publisher of the HorsIndex website. HorsIndex is currently a
            presentation and resource website dedicated to digital exposure:
            the site's form is used solely for contact requests.
          </p>
        ),
      },
      {
        h: "Data collected",
        body: (
          <>
            <p className="mb-4">
              Data collected via the forms is strictly limited to the following:
            </p>
            <ul className="space-y-2 list-none">
              <li className="flex gap-3"><span className="text-[#8B8F95]">-</span> Last and first name</li>
              <li className="flex gap-3"><span className="text-[#8B8F95]">-</span> Email address</li>
              <li className="flex gap-3"><span className="text-[#8B8F95]">-</span> Link, URL or entity concerned, provided freely (optional)</li>
              <li className="flex gap-3"><span className="text-[#8B8F95]">-</span> Message or context written freely</li>
            </ul>
          </>
        ),
      },
      {
        h: "Purposes",
        body: (
          <p>
            This data is collected for the sole purpose of contacting the data
            subject and responding to their request. It is not used for
            commercial prospecting.
          </p>
        ),
      },
      {
        h: "Legal basis",
        body: (
          <p>
            Processing is based on the explicit consent of the data subject, materialised
            by the voluntary submission of the form, and on the controller's legitimate
            interest in responding to inquiries.
          </p>
        ),
      },
      {
        h: "Retention period",
        body: (
          <p>
            Messages received are retained for the period strictly necessary to
            handle the request, without exceeding three years from the last
            exchange.
          </p>
        ),
      },
      {
        h: "Rights of the data subject",
        body: (
          <>
            <p className="mb-4">
              Pursuant to the General Data Protection Regulation (GDPR), each person has
              the following rights:
            </p>
            <ul className="space-y-2 list-none">
              <li className="flex gap-3"><span className="text-[#8B8F95]">-</span> Right of access</li>
              <li className="flex gap-3"><span className="text-[#8B8F95]">-</span> Right to rectification</li>
              <li className="flex gap-3"><span className="text-[#8B8F95]">-</span> Right to object</li>
              <li className="flex gap-3"><span className="text-[#8B8F95]">-</span> Right to restriction</li>
              <li className="flex gap-3"><span className="text-[#8B8F95]">-</span> Right to erasure</li>
              <li className="flex gap-3"><span className="text-[#8B8F95]">-</span> Right to portability</li>
              <li className="flex gap-3"><span className="text-[#8B8F95]">-</span> Right to lodge a complaint with the competent supervisory authority</li>
            </ul>
          </>
        ),
      },
      {
        h: "Exercising rights",
        body: (
          <p>
            These rights can be exercised by email to{" "}
            <a href="mailto:horsindex@gmail.com" className="text-[#181A1D] underline-link">horsindex@gmail.com</a>. Proof of identity may be
            requested to confirm the legitimacy of the request.
          </p>
        ),
      },
      {
        h: "Cookies",
        body: (
          <p>
            The site does not use advertising cookies or commercial profiling trackers.
            Technical cookies strictly necessary for the operation of the site may be used.
          </p>
        ),
      },
    ],
  },
};

export default function PolitiqueConfidentialite() {
  const { lang, t } = useT();
  const c = CONTENT[lang] || CONTENT.fr;

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <div data-testid="page-politique" className="bg-[#FAF9F7] text-[#181A1D] min-h-screen">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 lg:px-10 pt-32 lg:pt-40 pb-24">
        <Link
          to="/"
          data-testid="back-home-link"
          className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#666A70] hover:text-[#181A1D] mb-12 transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> {t("common.back")}
        </Link>

        <p className="label-eyebrow mb-6">{c.eyebrow}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#181A1D] tracking-tight leading-[1.05] font-normal mb-7 text-balance">
          {c.title}
        </h1>
        <div className="h1-marker mb-12" />

        <div className="space-y-12 font-normal text-[#52565C] leading-relaxed">
          {c.sections.map((s) => (
            <section key={s.h}>
              <h2 className="font-serif text-2xl text-[#181A1D] mb-4">{s.h}</h2>
              {s.body}
            </section>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
